// 期間計算ユーティリティ
//
// 月途中の固定費・人件費・家賃の日割り対応。
//   選択期間が今日を含む場合（当月、当週、年度の現在進行月など）は経過日数で按分する。
//   月額固定値 ÷ 30 × 実日数 を採用（家賃・固定費は契約上の月額のため、月の実日数 28〜31 ではなく 30 日基準）。

export interface DateRange {
  from: string; // 'YYYY-MM-DD'
  to: string; // 'YYYY-MM-DD'
}

const pad = (n: number): string => String(n).padStart(2, '0');

export const todayStr = (today: Date = new Date()): string =>
  `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

export const monthLastDay = (yearMonth: string): number => {
  const [y, m] = yearMonth.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

export const monthDateRange = (yearMonth: string): DateRange => ({
  from: `${yearMonth}-01`,
  to: `${yearMonth}-${pad(monthLastDay(yearMonth))}`,
});

export const fiscalYearRange = (
  fiscalYear: number,
  fiscalStartMonth: number = 8,
): DateRange => {
  // 年度開始: fiscalYear 年の fiscalStartMonth 月 1 日
  // 年度終了: 翌年の fiscalStartMonth-1 月末日
  const fromY = fiscalYear;
  const fromM = fiscalStartMonth;
  const toY = fiscalStartMonth === 1 ? fiscalYear : fiscalYear + 1;
  const toM = fiscalStartMonth === 1 ? 12 : fiscalStartMonth - 1;
  const toLast = new Date(Date.UTC(toY, toM, 0)).getUTCDate();
  return {
    from: `${fromY}-${pad(fromM)}-01`,
    to: `${toY}-${pad(toM)}-${pad(toLast)}`,
  };
};

export const calendarYearRange = (year: number): DateRange => ({
  from: `${year}-01-01`,
  to: `${year}-12-31`,
});

/**
 * 日付範囲内に含まれる「実経過日数」を返す。
 * 範囲が今日を超えている場合は今日までで切る（未来日は除外）。
 * 既に過去の範囲なら範囲内日数をそのまま。
 */
export const effectiveDayCount = (
  range: DateRange,
  today: Date = new Date(),
): number => {
  const t = todayStr(today);
  const effectiveTo = range.to < t ? range.to : t;
  if (effectiveTo < range.from) return 0;
  const fromMs = new Date(`${range.from}T00:00:00Z`).getTime();
  const toMs = new Date(`${effectiveTo}T00:00:00Z`).getTime();
  return Math.floor((toMs - fromMs) / 86400000) + 1;
};

/**
 * 月額固定値を期間に按分するための係数。
 *   実日数 / 30 を返す（月額の標準日数を 30 とする）
 *   範囲内に未来日があれば実日数から除外
 */
export const monthlyShare = (range: DateRange, today: Date = new Date()): number =>
  effectiveDayCount(range, today) / 30;

/**
 * 期間内の月の配列（YYYY-MM）を返す。
 */
export const monthsInRange = (range: DateRange): string[] => {
  const [fy, fm] = range.from.split('-').map(Number);
  const [ty, tm] = range.to.split('-').map(Number);
  const out: string[] = [];
  let y = fy;
  let m = fm;
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${pad(m)}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
};

/**
 * 期間内の各月の有効日数（その月内で範囲内かつ今日以前の日数）を返す。
 *   過去月: その月の総日数
 *   現在月: 1日〜今日（または範囲の終端、いずれか早い方）
 *   未来月: 0
 */
export const monthlyDaysInRange = (
  range: DateRange,
  today: Date = new Date(),
): Record<string, number> => {
  const result: Record<string, number> = {};
  const yms = monthsInRange(range);
  const t = todayStr(today);
  for (const ym of yms) {
    const monthRange = monthDateRange(ym);
    // 月範囲と target range の重なり
    const overlapFrom = monthRange.from > range.from ? monthRange.from : range.from;
    const overlapTo = monthRange.to < range.to ? monthRange.to : range.to;
    if (overlapFrom > overlapTo) {
      result[ym] = 0;
      continue;
    }
    // さらに今日で切る
    const effectiveTo = overlapTo < t ? overlapTo : t;
    if (effectiveTo < overlapFrom) {
      result[ym] = 0;
      continue;
    }
    const fromMs = new Date(`${overlapFrom}T00:00:00Z`).getTime();
    const toMs = new Date(`${effectiveTo}T00:00:00Z`).getTime();
    result[ym] = Math.floor((toMs - fromMs) / 86400000) + 1;
  }
  return result;
};

// 週関連
export const isoWeekRange = (isoWeek: string): DateRange => {
  const [yStr, wStr] = isoWeek.split('-W');
  const year = Number(yStr);
  const week = Number(wStr);
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay();
  const isoWeekStart = new Date(simple);
  if (dayOfWeek <= 4) isoWeekStart.setUTCDate(simple.getUTCDate() - dayOfWeek + 1);
  else isoWeekStart.setUTCDate(simple.getUTCDate() + 8 - dayOfWeek);
  const end = new Date(isoWeekStart);
  end.setUTCDate(end.getUTCDate() + 6);
  return {
    from: isoWeekStart.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
};

export const dateToIsoWeek = (date: Date): string => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const week =
    1 +
    Math.round(
      ((target.getTime() - firstThursday.getTime()) / 86400000 -
        3 +
        ((firstThursday.getUTCDay() + 6) % 7)) /
        7,
    );
  return `${target.getUTCFullYear()}-W${pad(week)}`;
};

export const dateStrToIsoWeek = (dateStr: string): string =>
  dateToIsoWeek(new Date(`${dateStr}T00:00:00Z`));

export const formatRangeLabel = (range: DateRange): string => {
  const fmt = (d: string) => {
    const [y, m, dd] = d.split('-');
    return `${y}/${Number(m)}/${Number(dd)}`;
  };
  return `${fmt(range.from)} 〜 ${fmt(range.to)}`;
};
